package com.eladfinish.bible.contest.network

import com.eladfinish.bible.contest.common.classNameOf
import io.github.aakira.napier.Napier
import io.ktor.client.call.body
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.appendPathSegments
import io.ktor.http.contentType

internal suspend inline fun <reified D, reified E> apiResult(apiCall: () -> HttpResponse): ApiResult<D, E> =
    try {
        ApiResult.Success(apiCall().body())
    } catch (e: Throwable) {
        (e as? ClientRequestException)?.apiError() ?: ApiResult.Failure(e)
    }

internal suspend inline fun <reified E> apiTextResult(apiCall: () -> HttpResponse): ApiResult<String, E> =
    try {
        ApiResult.Success(apiCall().bodyAsText())
    } catch (e: Throwable) {
        Napier.d("apiTextResult $e")
        (e as? ClientRequestException)?.apiError() ?: ApiResult.Failure(e)
    }

internal suspend inline fun <reified D, reified E> apiResultUTF8(apiCall: () -> HttpResponse): ApiResult<D, E> =
    try {
        ApiResult.Success(apiCall().bodyUTF8())
    } catch (e: Throwable) {
        Napier.d("apiResultUTF8 $e")
        (e as? ClientRequestException)?.apiError() ?: ApiResult.Failure(e)
    }

internal suspend inline fun <reified D> simplifiedApiResultUTF8(apiCall: () -> HttpResponse): Result<D> =
    try {
        Result.success(apiCall().bodyUTF8())
    } catch (e: Throwable) {
        Napier.d("apiResultUTF8 $e")
        Result.failure(e)
    }

internal suspend inline fun <reified D, reified E> apiResultUTF16(apiCall: () -> HttpResponse): ApiResult<D, E> =
    try {
        ApiResult.Success(apiCall().bodyUTF16())
    } catch (e: Throwable) {
        Napier.d("apiResultUTF16 $e")
        (e as? ClientRequestException)?.apiError() ?: ApiResult.Failure(e)
    }

internal suspend inline fun <reified E> apiTextResultUtf16Bom(apiCall: () -> HttpResponse): ApiResult<String, E> =
    try {
        ApiResult.Success(apiCall().bodyAsTextUtf16Bom())
    } catch (e: Throwable) {
        Napier.d("apiTextResultUtf16Bom $e")
        (e as? ClientRequestException)?.apiError() ?: ApiResult.Failure(e)
    }

internal suspend inline fun <D, reified E> ClientRequestException.apiError(): ApiResult<D, E> {
    val body = try {
        response.body<E>()
    } catch (e: Throwable) {
        null
    }

    return ApiResult.ClientError(response.status.value, body, this)
}

internal inline fun <reified T> HttpRequestBuilder.setJsonBody(body: T) {
    contentType(ContentType.Application.Json)
    setBody(body)
}

internal fun HttpRequestBuilder.appendPathSegments(vararg components: String) {
    url {
        appendPathSegments(*components)
    }
}

// Checks if a Throwable represents a network connectivity failure (no internet, DNS failure, etc.)
// Uses class name matching so it works across Android and iOS without platform-specific imports.
fun Throwable.isNetworkConnectivityError(): Boolean {
    val name = classNameOf(this::class)
    return "IOException" in name || "UnknownHostException" in name ||
            "ConnectException" in name || "SocketException" in name ||
            (cause?.isNetworkConnectivityError() == true)
}
